import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Upload, Database, FileJson, CheckCircle2, XCircle } from 'lucide-react';
import ConnectorGrid from '../onboarding/ConnectorGrid';
import ConnectionForm from '../onboarding/ConnectionForm';
import TableSelector from '../onboarding/TableSelector';
import type {
  OnboardingState, ConnectorType, TableInfo, DictionaryResult, MappingTargetMode,
} from '../../types';

interface Props {
  mode: MappingTargetMode;
  setMode: (m: MappingTargetMode) => void;
  target: OnboardingState;
  setTarget: React.Dispatch<React.SetStateAction<OnboardingState>>;
  targetDictionary: DictionaryResult | null;
  setTargetDictionary: (d: DictionaryResult | null) => void;
  onNext: () => void;
  onBack: () => void;
}

const EXAMPLE_TARGET = {
  tables: [
    {
      table_name: 'dim_customer',
      columns: [
        { name: 'customer_key', data_type: 'BIGINT', nullable: false, primary_key: true },
        { name: 'full_name', data_type: 'VARCHAR(200)', nullable: false, primary_key: false },
        { name: 'email', data_type: 'VARCHAR(255)', nullable: true, primary_key: false },
        { name: 'date_of_birth', data_type: 'DATE', nullable: true, primary_key: false },
      ],
    },
  ],
};

const TargetInputStep: React.FC<Props> = ({
  mode, setMode, target, setTarget, targetDictionary, setTargetDictionary, onNext, onBack,
}) => {
  const [jsonText, setJsonText] = useState<string>(JSON.stringify(EXAMPLE_TARGET, null, 2));
  const [jsonError, setJsonError] = useState<string>('');
  const [targetSubStep, setTargetSubStep] = useState<'connector' | 'connection' | 'tables'>('connector');

  const validateJson = (text: string): DictionaryResult | null => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed.tables || !Array.isArray(parsed.tables)) {
        setJsonError('JSON must have a top-level "tables" array');
        return null;
      }
      setJsonError('');
      return parsed as DictionaryResult;
    } catch (e: any) {
      setJsonError(e.message);
      return null;
    }
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      setJsonText(text);
      const parsed = validateJson(text);
      if (parsed) setTargetDictionary(parsed);
    };
    reader.readAsText(file);
  };

  const handlePasteContinue = () => {
    const parsed = validateJson(jsonText);
    if (parsed) {
      setTargetDictionary(parsed);
      onNext();
    }
  };

  // ── Target-connection sub-flow setters (reuse ConnectionForm/TableSelector) ─
  const setTargetConnectorType = (connectorType: ConnectorType) => {
    setTarget((s) => ({ ...s, connectorType, connection: { connector_type: connectorType } }));
    setTargetSubStep('connection');
  };

  const setTargetConnection = (connection: OnboardingState['connection'], tested: boolean) =>
    setTarget((s) => ({ ...s, connection, connectionTested: tested }));

  const setTargetSchema = (schema: string) => setTarget((s) => ({ ...s, selectedSchema: schema }));
  const setTargetTables = (tables: TableInfo[]) => setTarget((s) => ({ ...s, selectedTables: tables }));
  const setTargetColumns = (cols: Record<string, any>) => setTarget((s) => ({ ...s, columns: cols }));

  const canContinue = () => {
    if (mode === 'connection') {
      return target.connectionTested && target.selectedSchema && target.selectedTables.length > 0;
    }
    return !!targetDictionary && !jsonError;
  };

  return (
    <div className="space-y-6">
      {/* Mode selector */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg p-6">
        <h3 className="text-cargo-text font-semibold mb-1">Provide Target Schema</h3>
        <p className="text-cargo-muted text-sm mb-4">Choose how you want to supply the target schema</p>

        <div className="grid grid-cols-3 gap-3">
          <ModeCard
            active={mode === 'connection'}
            onClick={() => setMode('connection')}
            icon={<Database className="w-5 h-5" />}
            title="Connect Database"
            desc="Introspect a live target DB"
          />
          <ModeCard
            active={mode === 'upload'}
            onClick={() => setMode('upload')}
            icon={<Upload className="w-5 h-5" />}
            title="Upload Target Schema File"
            desc="Pick a target schema JSON from disk"
          />
          <ModeCard
            active={mode === 'paste'}
            onClick={() => setMode('paste')}
            icon={<FileJson className="w-5 h-5" />}
            title="Paste JSON"
            desc="Paste a schema JSON inline"
          />
        </div>
      </div>

      {/* Mode bodies */}
      {mode === 'connection' && (
        <div className="space-y-6">
          {targetSubStep === 'connector' && !target.connectorType && (
            <ConnectorGrid onSelect={setTargetConnectorType} selected={target.connectorType} />
          )}

          {targetSubStep === 'connection' && target.connectorType && (
            <ConnectionForm
              state={target}
              setConnection={setTargetConnection}
              onNext={() => setTargetSubStep('tables')}
              onBack={() => {
                setTarget((s) => ({ ...s, connectorType: null, connection: {} }));
                setTargetSubStep('connector');
              }}
            />
          )}

          {targetSubStep === 'tables' && target.connectionTested && (
            <TableSelector
              state={target}
              setSchema={setTargetSchema}
              setTables={setTargetTables}
              setColumns={setTargetColumns}
              onNext={onNext}
              onBack={() => setTargetSubStep('connection')}
            />
          )}
        </div>
      )}

      {mode === 'upload' && (
        <div className="bg-cargo-panel border border-cargo-border rounded-lg p-6">
          <label className="block">
            <div className="border-2 border-dashed border-cargo-border rounded-lg p-10 text-center cursor-pointer hover:bg-cargo-hover/20 transition-colors">
              <Upload className="w-10 h-10 text-cargo-muted mx-auto mb-3" />
              <p className="text-cargo-text font-medium">Click to upload target schema JSON</p>
              <p className="text-cargo-muted text-xs mt-1">Must be a JSON object with a "tables" array</p>
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              />
            </div>
          </label>
          {targetDictionary && (
            <div className="mt-4 flex items-center gap-2 text-cargo-success text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Loaded {targetDictionary.tables.length} target table(s)
            </div>
          )}
          {jsonError && (
            <div className="mt-4 flex items-center gap-2 text-cargo-error text-sm">
              <XCircle className="w-4 h-4" />
              {jsonError}
            </div>
          )}
        </div>
      )}

      {mode === 'paste' && (
        <div className="bg-cargo-panel border border-cargo-border rounded-lg p-6">
          <label className="block text-cargo-text text-sm font-medium mb-2">Target Schema JSON</label>
          <textarea
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              validateJson(e.target.value);
            }}
            className="w-full h-80 px-3 py-2 bg-cargo-surface border border-cargo-border rounded-lg text-cargo-text text-xs font-mono focus:outline-none focus:ring-2 focus:ring-cargo-accent"
            spellCheck={false}
          />
          {jsonError ? (
            <div className="mt-2 flex items-center gap-2 text-cargo-error text-xs">
              <XCircle className="w-3.5 h-3.5" />
              {jsonError}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-cargo-success text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Valid JSON
            </div>
          )}
        </div>
      )}

      {/* Nav — only shown for upload/paste modes.
          In connection mode, TableSelector renders its own Back/Next buttons
          (reused from onboarding wizard), so we hide the outer nav to avoid duplication. */}
      {mode !== 'connection' && (
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={mode === 'paste' ? handlePasteContinue : onNext}
          disabled={mode === 'paste' ? !!jsonError : !targetDictionary}
          className="inline-flex items-center gap-2 px-5 py-2 bg-cargo-accent text-white rounded-lg hover:bg-cargo-accent/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      )}
    </div>
  );
};

const ModeCard: React.FC<{
  active: boolean; onClick: () => void; icon: React.ReactNode; title: string; desc: string;
}> = ({ active, onClick, icon, title, desc }) => (
  <button
    onClick={onClick}
    className={`text-left p-4 rounded-lg border transition-all ${
      active
        ? 'border-cargo-accent bg-cargo-accent/10 text-cargo-text'
        : 'border-cargo-border hover:border-cargo-accent/50 text-cargo-muted hover:text-cargo-text'
    }`}
  >
    <div className={`${active ? 'text-cargo-accent' : 'text-cargo-muted'} mb-2`}>{icon}</div>
    <div className="font-medium text-sm mb-0.5">{title}</div>
    <div className="text-xs opacity-80">{desc}</div>
  </button>
);

export default TargetInputStep;
